<template>
  <div>
    <v-container>
      <base-material-card
        icon="mdi-book-open-page-variant"
        title="ข้อมูลคู่มือการใช้งาน"
        class="px-5 py-3"
      >
        <!-- ส่วนค้นหาและปุ่มเพิ่ม -->
        <v-card class="mb-4 pa-3" flat outlined>
          <v-row align="center">
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="ค้นหา ระบุคำ หรือ ส่วนข้อความเกี่ยวข้อง"
                single-line
                hide-details
                dense
                filled
                rounded
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" class="text-sm-right">
              <v-btn
                large
                depressed
                color="primary"
                @click="openAddDialog()"
              >
                <v-icon left>mdi-plus-circle-outline</v-icon>
                เพิ่มรายการ
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <!-- ตารางแสดงข้อมูล -->
        <v-data-table
          :loading="loading"
          loading-text="กำลังโหลดข้อมูล..."
          :headers="headers"
          :items="manuals"
          :search="search"
          :items-per-page="25"
          :footer-props="{
            'items-per-page-options': [25, 50, 100, -1],
            'items-per-page-text': 'แสดงต่อหน้า',
          }"
          class="elevation-0"
        >
          <!-- แสดงลำดับ -->
          <template v-slot:item.index="{ index }">
            {{ index + 1 }}
          </template>

          <!-- แสดงไฟล์แนบเป็นลิงก์ -->
          <template v-slot:item.link_file="{ item }">
            <v-btn
              v-if="item.link_file"
              small
              text
              color="primary"
              @click="openFilePreview(item)"
            >
              <v-icon left small>mdi-file-pdf-box</v-icon>
              ดูไฟล์
            </v-btn>
            <v-chip v-else x-small color="grey lighten-2">
              ไม่มีไฟล์
            </v-chip>
          </template>

          <!-- แสดงวันที่ในรูปแบบไทย -->
          <template v-slot:item.date_time="{ item }">
            {{ formatDate(item.date_time) }}
          </template>

          <!-- ปุ่มแก้ไข -->
          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="amber darken-2"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="openEditDialog(item.id_manual)"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>แก้ไข</span>
            </v-tooltip>
          </template>

          <!-- ปุ่มลบ -->
          <template v-slot:item.action_s="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="red"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="openDeleteDialog(item.id_manual)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>ลบ</span>
            </v-tooltip>
          </template>

          <!-- ไม่พบผลลัพธ์ -->
          <template v-slot:no-results>
            <v-alert type="warning" text class="ma-2">
              ไม่พบผลลัพธ์ "{{ search }}" ที่คุณกำลังค้นหา
            </v-alert>
          </template>

          <!-- ไม่มีข้อมูล -->
          <template v-slot:no-data>
            <v-alert type="info" text class="ma-2">
              ยังไม่มีข้อมูลคู่มือการใช้งาน
            </v-alert>
          </template>
        </v-data-table>
      </base-material-card>

      <!-- =============================== -->
      <!-- Dialog เพิ่มข้อมูล -->
      <!-- =============================== -->
      <v-dialog v-model="addDialog" persistent max-width="600">
        <v-card>
          <v-toolbar flat color="primary" dark>
            <v-icon left>mdi-plus-circle</v-icon>
            <v-toolbar-title>เพิ่มข้อมูลคู่มือการใช้งาน</v-toolbar-title>
            <v-spacer />
            <v-btn icon @click="addDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="pt-5">
            <v-form ref="addForm" lazy-validation>
              <v-text-field
                v-model="addForm.topic_s"
                label="หัวข้อ *"
                prepend-icon="mdi-format-title"
                :rules="[rules.required]"
                outlined
                dense
              />

              <v-textarea
                v-model="addForm.detail_s"
                label="รายละเอียด *"
                prepend-icon="mdi-text-box-outline"
                :rules="[rules.required]"
                outlined
                dense
                rows="3"
                auto-grow
              />

              <v-file-input
                v-model="addFile"
                label="แนบไฟล์ PDF"
                prepend-icon="mdi-file-pdf-box"
                accept="application/pdf"
                show-size
                outlined
                dense
                :rules="[rules.pdfOnly]"
                hint="รองรับเฉพาะไฟล์ PDF เท่านั้น"
                persistent-hint
              />

              <v-text-field
                v-model="addForm.date_time"
                label="วันที่ *"
                prepend-icon="mdi-calendar"
                type="date"
                :rules="[rules.required]"
                outlined
                dense
              />
            </v-form>
          </v-card-text>

          <v-divider />

          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn
              color="grey"
              text
              large
              @click="addDialog = false"
            >
              <v-icon left>mdi-close</v-icon>
              ยกเลิก
            </v-btn>
            <v-btn
              color="success"
              depressed
              large
              :loading="saving"
              @click="submitAdd()"
            >
              <v-icon left>mdi-content-save</v-icon>
              บันทึก
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- =============================== -->
      <!-- Dialog แก้ไขข้อมูล -->
      <!-- =============================== -->
      <v-dialog v-model="editDialog" persistent max-width="600">
        <v-card>
          <v-toolbar flat color="amber darken-2" dark>
            <v-icon left>mdi-pencil</v-icon>
            <v-toolbar-title>แก้ไขข้อมูลคู่มือการใช้งาน</v-toolbar-title>
            <v-spacer />
            <v-btn icon @click="editDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="pt-5">
            <v-form ref="editForm" lazy-validation>
              <v-text-field
                v-model="editFormData.topic_s"
                label="หัวข้อ *"
                prepend-icon="mdi-format-title"
                :rules="[rules.required]"
                outlined
                dense
              />

              <v-textarea
                v-model="editFormData.detail_s"
                label="รายละเอียด *"
                prepend-icon="mdi-text-box-outline"
                :rules="[rules.required]"
                outlined
                dense
                rows="3"
                auto-grow
              />

              <!-- แสดงไฟล์ปัจจุบัน -->
              <v-alert
                v-if="editFormData.link_file"
                type="info"
                text
                dense
                class="mb-3"
              >
                <v-row align="center" no-gutters>
                  <v-col>
                    <strong>ไฟล์ปัจจุบัน:</strong> {{ editFormData.link_file }}
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      x-small
                      text
                      color="primary"
                      @click="previewFile(editFormData.link_file)"
                    >
                      <v-icon x-small left>mdi-eye</v-icon>
                      ดูไฟล์
                    </v-btn>
                  </v-col>
                </v-row>
              </v-alert>

              <v-file-input
                v-model="editFile"
                label="เปลี่ยนไฟล์ PDF (ถ้าต้องการ)"
                prepend-icon="mdi-file-pdf-box"
                accept="application/pdf"
                show-size
                outlined
                dense
                :rules="[rules.pdfOnlyOptional]"
                hint="เลือกไฟล์ใหม่เฉพาะเมื่อต้องการเปลี่ยน"
                persistent-hint
                clearable
              />

              <v-text-field
                v-model="editFormData.date_time"
                label="วันที่ *"
                prepend-icon="mdi-calendar"
                type="date"
                :rules="[rules.required]"
                outlined
                dense
              />
            </v-form>
          </v-card-text>

          <v-divider />

          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn color="grey" text large @click="editDialog = false">
              <v-icon left>mdi-close</v-icon>
              ยกเลิก
            </v-btn>
            <v-btn
              color="amber darken-2"
              depressed
              large
              dark
              :loading="saving"
              @click="submitEdit()"
            >
              <v-icon left>mdi-pencil</v-icon>
              บันทึก
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- =============================== -->
      <!-- Dialog ลบข้อมูล -->
      <!-- =============================== -->
      <v-dialog v-model="deleteDialog" persistent max-width="450">
        <v-card>
          <v-toolbar flat color="red darken-2" dark>
            <v-icon left>mdi-delete-alert</v-icon>
            <v-toolbar-title>ยืนยันการลบ</v-toolbar-title>
          </v-toolbar>

          <v-card-text class="pt-5 text-center">
            <v-icon size="64" color="red lighten-2" class="mb-3">
              mdi-alert-circle-outline
            </v-icon>
            <h3 class="mb-2">คุณต้องการลบคู่มือนี้หรือไม่?</h3>
            <p class="subtitle-1 grey--text">
              "{{ editFormData.topic_s }}"
            </p>
            <v-alert type="warning" text dense class="mt-3">
              การลบจะไม่สามารถกู้คืนได้
            </v-alert>
          </v-card-text>

          <v-divider />

          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn text large @click="deleteDialog = false">
              <v-icon left>mdi-close</v-icon>
              ยกเลิก
            </v-btn>
            <v-btn
              color="red darken-2"
              depressed
              large
              dark
              :loading="saving"
              @click="submitDelete()"
            >
              <v-icon left>mdi-delete</v-icon>
              ลบข้อมูล
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- =============================== -->
      <!-- Dialog แสดงไฟล์ PDF -->
      <!-- =============================== -->
      <v-dialog v-model="filePreviewDialog" max-width="900" scrollable>
        <v-card>
          <v-toolbar flat color="primary" dark dense>
            <v-icon left>mdi-file-pdf-box</v-icon>
            <v-toolbar-title>{{ previewTitle }}</v-toolbar-title>
            <v-spacer />
            <v-btn icon @click="downloadFile(previewFilePath)" title="ดาวน์โหลด">
              <v-icon>mdi-download</v-icon>
            </v-btn>
            <v-btn icon @click="filePreviewDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="pa-0" style="height: 75vh;">
            <iframe
              v-if="previewFilePath"
              :src="fileBasePath + previewFilePath"
              width="100%"
              height="100%"
              frameborder="0"
              style="border: none;"
            />
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- =============================== -->
      <!-- Snackbar แจ้งเตือน -->
      <!-- =============================== -->
      <v-snackbar
        v-model="snackbar.show"
        top
        :timeout="snackbar.timeout"
        :color="snackbar.color"
      >
        <v-icon left>{{ snackbar.icon }}</v-icon>
        {{ snackbar.text }}

        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">
            ปิด
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
export default {
  data() {
    return {
      // ตั้งค่าระบบ
      ApiKey: "HRvec2021",
      fileBasePath: "../HRvecfiles/",
      loading: true,
      saving: false,
      search: "",

      // ข้อมูลตาราง
      manuals: [],
      headers: [       
        { text: "หัวข้อ", align: "left", value: "topic_s" },
        { text: "รายละเอียด", align: "left", value: "detail_s" },
        { text: "ไฟล์แนบ", align: "center", value: "link_file", sortable: false, width: "120" },
        { text: "วันที่", align: "center", value: "date_time", width: "130" },
        { text: "แก้ไข", align: "center", value: "actions", sortable: false, width: "80" },
        { text: "ลบ", align: "center", value: "action_s", sortable: false, width: "80" },
      ],

      // Dialog states
      addDialog: false,
      editDialog: false,
      deleteDialog: false,
      filePreviewDialog: false,

      // ฟอร์มเพิ่มข้อมูล
      addForm: {},
      addFile: null,

      // ฟอร์มแก้ไขข้อมูล
      editFormData: {},
      editFile: null,

      // ตัวอย่างไฟล์
      previewFilePath: "",
      previewTitle: "",

      // Snackbar
      snackbar: {
        show: false,
        color: "",
        timeout: 4000,
        icon: "",
        text: "",
      },

      // Validation rules
      rules: {
        required: (v) => !!v || "กรุณากรอกข้อมูล",
        pdfOnly: (v) => {
          if (!v) return true;
          return v.type === "application/pdf" || "กรุณาเลือกไฟล์ PDF เท่านั้น";
        },
        pdfOnlyOptional: (v) => {
          if (!v) return true;
          return v.type === "application/pdf" || "กรุณาเลือกไฟล์ PDF เท่านั้น";
        },
      },
    };
  },

  async mounted() {
    await this.fetchManuals();
  },

  computed: {
    timeStamp() {
      return Date.now();
    },
  },

  methods: {
    // ============ ดึงข้อมูล ============

    async fetchManuals() {
      this.loading = true;
      try {
        const result = await this.$http.post("manual_s.php", {
          ApiKey: this.ApiKey,
        });
        this.manuals = result.data;
      } catch (error) {
        this.showSnackbar("error", "mdi-alert-circle", "ไม่สามารถโหลดข้อมูลได้");
      } finally {
        this.loading = false;
      }
    },

    // ============ เพิ่มข้อมูล ============

    openAddDialog() {
      this.addForm = {
        topic_s: "",
        detail_s: "",
        date_time: new Date().toISOString().substr(0, 10),
      };
      this.addFile = null;
      if (this.$refs.addForm) this.$refs.addForm.resetValidation();
      this.addDialog = true;
    },

    async submitAdd() {
      if (!this.$refs.addForm.validate()) return;

      this.saving = true;
      try {
        // อัปโหลดไฟล์ (ถ้ามี)
        if (this.addFile) {
          const uploaded = await this.uploadFile(this.addFile, this.addForm.topic_s);
          if (uploaded) {
            this.addForm.link_file = uploaded;
          } else {
            this.showSnackbar("error", "mdi-close-circle", "อัปโหลดไฟล์ไม่สำเร็จ");
            this.saving = false;
            return;
          }
        }

        // บันทึกข้อมูล
        this.addForm.ApiKey = this.ApiKey;
        const result = await this.$http.post("manual_s.insert.php", this.addForm);

        if (result.data.status) {
          this.showSnackbar("success", "mdi-check-circle", "เพิ่มข้อมูลเรียบร้อย");
          await this.fetchManuals();
          this.addDialog = false;
        } else {
          this.showSnackbar("error", "mdi-close-circle", "บันทึกข้อมูลผิดพลาด");
        }
      } catch (error) {
        this.showSnackbar("error", "mdi-alert-circle", "เกิดข้อผิดพลาด กรุณาลองใหม่");
      } finally {
        this.saving = false;
      }
    },

    // ============ แก้ไขข้อมูล ============

    async openEditDialog(id_manual) {
      try {
        const result = await this.$http.post("manual_s.php", {
          ApiKey: this.ApiKey,
          id_manual: id_manual,
        });
        this.editFormData = { ...result.data };
        this.editFile = null;
        if (this.$refs.editForm) this.$refs.editForm.resetValidation();
        this.editDialog = true;
      } catch (error) {
        this.showSnackbar("error", "mdi-alert-circle", "ไม่สามารถโหลดข้อมูลได้");
      }
    },

    async submitEdit() {
      if (!this.$refs.editForm.validate()) return;

      this.saving = true;
      try {
        // อัปโหลดไฟล์ใหม่ (ถ้ามี)
        if (this.editFile) {
          const uploaded = await this.uploadFile(this.editFile, this.editFormData.topic_s);
          if (uploaded) {
            // ลบไฟล์เก่า
            if (this.editFormData.link_file) {
              await this.$http.post("deletefile.php", {
                ApiKey: this.ApiKey,
                filename: this.fileBasePath + this.editFormData.link_file,
              });
            }
            this.editFormData.link_file = uploaded;
          } else {
            this.showSnackbar("error", "mdi-close-circle", "อัปโหลดไฟล์ไม่สำเร็จ");
            this.saving = false;
            return;
          }
        }

        // บันทึกข้อมูล
        this.editFormData.ApiKey = this.ApiKey;
        const result = await this.$http.post("manual_s.update.php", this.editFormData);

        if (result.data.status) {
          this.showSnackbar("success", "mdi-check-circle", "แก้ไขข้อมูลเรียบร้อย");
          await this.fetchManuals();
          this.editDialog = false;
        } else {
          this.showSnackbar("error", "mdi-close-circle", "แก้ไขข้อมูลผิดพลาด");
        }
      } catch (error) {
        this.showSnackbar("error", "mdi-alert-circle", "เกิดข้อผิดพลาด กรุณาลองใหม่");
      } finally {
        this.saving = false;
      }
    },

    // ============ ลบข้อมูล ============

    async openDeleteDialog(id_manual) {
      try {
        const result = await this.$http.post("manual_s.php", {
          ApiKey: this.ApiKey,
          id_manual: id_manual,
        });
        this.editFormData = { ...result.data };
        this.deleteDialog = true;
      } catch (error) {
        this.showSnackbar("error", "mdi-alert-circle", "ไม่สามารถโหลดข้อมูลได้");
      }
    },

    async submitDelete() {
      this.saving = true;
      try {
        // ลบไฟล์แนบ
        if (this.editFormData.link_file) {
          await this.$http.post("deletefile.php", {
            ApiKey: this.ApiKey,
            filename: this.fileBasePath + this.editFormData.link_file,
          });
        }

        // ลบข้อมูล
        this.editFormData.ApiKey = this.ApiKey;
        const result = await this.$http.post("manual_s.delete.php", this.editFormData);

        if (result.data.status) {
          Swal.fire({
            icon: "success",
            title: "ลบข้อมูลเรียบร้อย",
            showConfirmButton: false,
            timer: 1500,
          });
          await this.fetchManuals();
        } else {
          Swal.fire({
            icon: "warning",
            title: "ดำเนินการผิดพลาด",
            showConfirmButton: false,
            timer: 1500,
          });
        }
        this.deleteDialog = false;
      } catch (error) {
        this.showSnackbar("error", "mdi-alert-circle", "เกิดข้อผิดพลาด กรุณาลองใหม่");
      } finally {
        this.saving = false;
      }
    },

    // ============ จัดการไฟล์ ============

    async uploadFile(file, topicName) {
      try {
        const filename = topicName + "." + Date.now() + ".manual.pdf";
        const formData = new FormData();
        formData.append("file", file);
        formData.append("filename", this.fileBasePath + filename);
        formData.append("ApiKey", this.ApiKey);

        const result = await this.$http.post("uploadfile.php", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        return result.data.status ? filename : null;
      } catch (error) {
        return null;
      }
    },

    openFilePreview(item) {
      this.previewTitle = item.topic_s || "ไฟล์แนบ";
      this.previewFilePath = item.link_file;
      this.filePreviewDialog = true;
    },

    previewFile(filename) {
      this.previewTitle = filename;
      this.previewFilePath = filename;
      this.filePreviewDialog = true;
    },

    downloadFile(filename) {
      const link = document.createElement("a");
      link.href = this.fileBasePath + filename;
      link.download = filename;
      link.target = "_blank";
      link.click();
    },

    // ============ Utility ============

    formatDate(dateStr) {
      if (!dateStr) return "-";
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString("th-TH", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch {
        return dateStr;
      }
    },

    showSnackbar(color, icon, text) {
      this.snackbar = {
        show: true,
        color,
        icon,
        text,
        timeout: 4000,
      };
    },
  },
};
</script>